import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPinIcon, PhoneIcon, MailIcon, ClockIcon } from 'lucide-react';
import { CedarTreeIcon } from './icons/CedarTreeIcon';
export function AboutSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="about" className="py-24 bg-background relative overflow-hidden" ref={ref} data-id="element-9">
      {/* Decorative Watermark */}
      <div className="absolute -right-64 -bottom-64 opacity-[0.04] pointer-events-none rotate-12" data-id="element-10">
        <CedarTreeIcon className="w-[600px] h-[600px] text-[#00a850]" data-id="element-11" />
      </div>

      <div className="container mx-auto px-4 relative z-10" data-id="element-12">
        <div className="max-w-7xl mx-auto" data-id="element-13">
          <motion.div className="text-center mb-20" initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 30
        }} transition={{
          duration: 0.6
        }} data-id="element-14">
            <h2 className="text-3xl md:text-5xl font-serif font-bold text-foreground mb-6" data-id="element-15">
              Contact & Location
            </h2>
            <div className="w-24 h-1.5 bg-[#006233] mx-auto rounded-full" data-id="element-16"></div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-stretch" data-id="element-17">
            {/* Contact Information */}
            <motion.div className="bg-card rounded-2xl p-10 md:p-14 shadow-lg border border-border/50 flex flex-col justify-center relative overflow-hidden" initial={{
            opacity: 0,
            x: -50
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {
            opacity: 0,
            x: -50
          }} transition={{
            duration: 0.7,
            delay: 0.2,
            ease: 'easeOut'
          }} data-id="element-18">
              <div className="absolute left-0 top-0 w-1.5 h-full bg-[#006233]" data-id="element-19"></div>

              <h3 className="text-3xl font-serif font-bold text-foreground mb-10" data-id="element-20">
                Consulate General of Lebanon
              </h3>

              <ul className="space-y-8" data-id="element-21">
                <li className="flex items-start gap-6 pb-8 border-b border-border/60" data-id="element-22">
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-950/30 rounded-full text-[#006233] shrink-0 mt-1" data-id="element-23">
                    <MapPinIcon className="w-7 h-7" data-id="element-24" />
                  </div>
                  <div data-id="element-25">
                    <h4 className="text-lg font-bold text-foreground mb-2 uppercase tracking-wider" data-id="element-26">
                      Address
                    </h4>
                    <p className="text-muted-foreground text-lg leading-relaxed" data-id="element-27">
                      Level 13, 155 Castlereagh Street
                      <br data-id="element-28" />
                      SYDNEY NSW 2000
                      <br data-id="element-29" />
                      Australia
                    </p>
                  </div>
                </li>

                <li className="flex items-start gap-6 pb-8 border-b border-border/60" data-id="element-30">
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-950/30 rounded-full text-[#006233] shrink-0 mt-1" data-id="element-31">
                    <PhoneIcon className="w-7 h-7" data-id="element-32" />
                  </div>
                  <div data-id="element-33">
                    <h4 className="text-lg font-bold text-foreground mb-2 uppercase tracking-wider" data-id="element-34">
                      Phone
                    </h4>
                    <p className="text-muted-foreground text-lg" data-id="element-35">
                      <a href="tel:+61292337474" className="hover:text-[#006233] font-medium transition-colors" data-id="element-36">
                        +61 2 9233 7474
                      </a>
                    </p>
                  </div>
                </li>

                <li className="flex items-start gap-6 pb-8 border-b border-border/60" data-id="element-37">
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-950/30 rounded-full text-[#006233] shrink-0 mt-1" data-id="element-38">
                    <MailIcon className="w-7 h-7" data-id="element-39" />
                  </div>
                  <div data-id="element-40">
                    <h4 className="text-lg font-bold text-foreground mb-2 uppercase tracking-wider" data-id="element-41">
                      Email
                    </h4>
                    <p className="text-muted-foreground text-lg" data-id="element-42">
                      <a href="mailto:info@lebconsyd.org.au" className="hover:text-[#006233] font-medium transition-colors" data-id="element-43">
                        info@lebconsyd.org.au
                      </a>
                    </p>
                  </div>
                </li>

                <li className="flex items-start gap-6 pt-2" data-id="element-44">
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-950/30 rounded-full text-[#006233] shrink-0 mt-1" data-id="element-45">
                    <ClockIcon className="w-7 h-7" data-id="element-46" />
                  </div>
                  <div data-id="element-47">
                    <h4 className="text-lg font-bold text-foreground mb-2 uppercase tracking-wider" data-id="element-48">
                      Office Hours
                    </h4>
                    <div className="text-muted-foreground text-lg space-y-2" data-id="element-49">
                      <p data-id="element-50">
                        <span className="font-bold text-foreground" data-id="element-51">
                          Monday to Friday
                        </span>
                      </p>
                      <p data-id="element-52">Consular Services: 9:00 AM – 3:00 PM</p>
                      <p data-id="element-53">General Inquiries: 9:00 AM – 4:00 PM</p>
                      <p className="text-base text-[#ED1C24] font-medium mt-4 bg-red-50 dark:bg-red-950/20 p-3 rounded-lg inline-block" data-id="element-54">
                        Closed on Australian and Lebanese public holidays.
                      </p>
                    </div>
                  </div>
                </li>
              </ul>
            </motion.div>

            {/* Elevated Map Placeholder */}
            <motion.div className="bg-card rounded-2xl shadow-lg border border-border/50 overflow-hidden flex flex-col min-h-[500px] relative group" initial={{
            opacity: 0,
            x: 50
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {
            opacity: 0,
            x: 50
          }} transition={{
            duration: 0.7,
            delay: 0.4,
            ease: 'easeOut'
          }} data-id="element-55">
              <div className="flex-1 bg-gradient-to-br from-emerald-50 via-muted to-emerald-100/50 dark:from-emerald-950/20 dark:via-muted dark:to-emerald-900/10 flex items-center justify-center relative overflow-hidden" data-id="element-56">
                <svg className="absolute inset-0 w-full h-full opacity-20 text-[#006233]" xmlns="http://www.w3.org/2000/svg" data-id="element-57">
                  <path d="M0 100 Q 150 50 300 150 T 600 100" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-58" />
                  <path d="M0 200 Q 200 150 400 250 T 800 200" fill="none" stroke="currentColor" strokeWidth="1" data-id="element-59" />
                  <path d="M100 0 L 150 400" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="5,5" data-id="element-60" />
                  <path d="M300 0 L 250 500" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-61" />
                </svg>

                <div className="relative z-10 text-center p-10 bg-background/95 backdrop-blur-md rounded-2xl shadow-xl border border-border/50 max-w-sm mx-auto transform group-hover:scale-105 transition-transform duration-500" data-id="element-62">
                  <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner" data-id="element-63">
                    <MapPinIcon className="w-10 h-10 text-[#006233]" data-id="element-64" />
                  </div>
                  <h4 className="font-serif font-bold text-2xl text-foreground mb-3" data-id="element-65">
                    Sydney CBD
                  </h4>
                  <p className="text-muted-foreground text-lg mb-8 leading-relaxed" data-id="element-66">
                    Level 13, 155 Castlereagh Street
                    <br data-id="element-67" />
                    SYDNEY NSW 2000
                  </p>
                  <a href="https://maps.google.com/?q=155+Castlereagh+Street,+Sydney+NSW+2000" target="_blank" rel="noopener noreferrer" className="inline-block px-8 py-3 bg-[#006233] hover:bg-emerald-800 text-white text-base font-bold rounded-lg shadow-md hover:shadow-lg transition-all duration-300" data-id="element-68">
                    Get Directions
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}